import React, { useState } from 'react';
import { Search } from 'lucide-react';
import L from 'leaflet';
import * as turf from '@turf/turf';

const MapSearchBox = ({ map, createPolygonWithTooltip, setDrawnCoordinates, setActiveAOIPolygon }) => {

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState('');
  const [marker, setMarker] = useState(null); // New state for marker

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchQuery.trim() || !map) return;

    setIsSearching(true);
    setError('');

    try {
      const coordsRegex = /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/;

      let lat, lng;

      if (coordsRegex.test(searchQuery)) {
        [lat, lng] = searchQuery.split(',').map(coord => parseFloat(coord.trim()));
      } else {
        const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`);
        if (!response.ok) throw new Error('Search failed');

        const data = await response.json();
        if (data.length === 0) {
          setError('No results found');
          return;
        }

        lat = parseFloat(data[0].lat);
        lng = parseFloat(data[0].lon);
      }

      // Center map and add marker
      map.flyTo([lat, lng], 12, {
        animate: true,
        duration: 2.5,
        easeLinearity: 0.1,
        noMoveStart: false
      });
      
      
      // Create a 10km buffer box around the point
const center = turf.point([lng, lat]);
const buffered = turf.buffer(center, 10, { units: 'kilometers' });
const bbox = turf.bbox(buffered);
const squareAOI = turf.bboxPolygon(bbox);

const coords = squareAOI.geometry.coordinates[0].map(coord => ({
  lat: coord[1],
  lng: coord[0]
}));
const customEvent = new CustomEvent('aoiCoordinatesUpdated', {
  detail: { coordinates: coords }
});
window.dispatchEvent(customEvent);
// Clear previous polygon layers
map.eachLayer((layer) => {
  if (layer instanceof L.Polygon && !(layer instanceof L.Rectangle)) {
    map.removeLayer(layer);
  }
});

// Step 1: Get centroid for reverse geocoding
const centerLat = lat;
const centerLng = lng;

// Step 2: Try reverse geocoding
let shortLocationName = 'Search AOI';
try {
  const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${centerLat}&lon=${centerLng}`);
  const data = await res.json();
  const addr = data.address;
  shortLocationName = addr.city || addr.town || addr.village || addr.hamlet || addr.county || addr.state_district || addr.state || 'Search AOI';
} catch (err) {
  console.warn('Failed to reverse geocode search location', err);
}


// Draw new AOI polygon
const polygon = createPolygonWithTooltip(coords, shortLocationName);
setActiveAOIPolygon(polygon);
setDrawnCoordinates(coords);

// Trigger global event for new AOI feature


      // Remove existing marker
      if (marker) {
        map.removeLayer(marker);
      }

      const newMarker = L.marker([lat, lng]).addTo(map).bindPopup(`📍 ${searchQuery}`).openPopup();
      setMarker(newMarker);

    } catch (error) {
      console.error('Search error:', error);
      setError('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div style={{
      zIndex: 1000,
      position: 'fixed',
      top: '50px',
      left: '05px',
      backgroundColor: 'hsl(0,0%,0%)',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
      padding: '6px 6px',
      display: 'flex',
      alignItems: 'center',
      width: 'auto',
      transition: 'all 0.3s ease'
    }}>
      <form onSubmit={handleSearch} style={{ display: 'flex', width: '100%', position: 'relative' }}>
        <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#A6B6CE' }}>
          <Search size={16} />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search place or lat,lng"
          style={{
            padding: '8px 8px 8px 32px',
            backgroundColor: 'transparent',
            color: '#A6B6CE',
            border: '1px solid rgba(166, 182, 206, 0.3)',
            borderRadius: '4px',
            fontSize: '13px',
            width: '220px',
            outline: 'none'
          }}
        />
        <button
          type="submit"
          disabled={isSearching}
          style={{
            marginLeft: '8px',
            padding: '8px 12px',
            borderRadius: '4px',
            backgroundColor: '#2E90FA',
            color: 'white',
            border: 'none',
            cursor: isSearching ? 'default' : 'pointer',
            fontSize: '13px',
            fontWeight: '500',
            transition: 'background-color 0.2s ease',
            opacity: isSearching ? 0.8 : 1
          }}
        >
          {isSearching ? '...' : 'Go'}
        </button>
      </form>
      {error && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          width: '100%',
          backgroundColor: 'rgba(255, 77, 79, 0.9)',
          color: 'white',
          padding: '4px 12px',
          borderRadius: '0 0 8px 8px',
          fontSize: '12px',
          marginTop: '2px'
        }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default MapSearchBox;
